import React, { useState, useMemo, useCallback } from "react";
import { navigate } from "@reach/router";
import { Formik, Form } from "formik";
import { FiArrowLeft } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";
import { TabHeader } from "components/TabHeader";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { AutoSaveIndicator, AutoSaveProvider, FormikAutoSave } from "newui/components/ui/AutoSave";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { KeyPersonnelFullForm } from "./components/KeyPersonnelFullForm";
import { useKeyPersonnelDetail } from "./hooks/useKeyPersonnel";
import {
	useCreateKeyPersonnel,
	useUpdateKeyPersonnel,
	useDeleteKeyPersonnel,
} from "./hooks/useKeyPersonnelMutations";
import { buildFormData } from "./key-personnel-api";
import { createKeyPersonnelValidation } from "./validation/keyPersonnelValidation";
import type {
	KeyPersonnelFormValues,
	KeyPersonnelDetail,
} from "./types/KeyPersonnel.type";
import Tooltip from "components/Tooltip";
import { showToast } from "utils/helpers";
import BackButton from "newui/components/BackButton";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { useCurrentCompany, useCurrentCompanyId } from "hooks/auth";

interface KeyPersonnelFormPageProps {
	id?: string;
	path?: string;
}

const emptyInitialValues: KeyPersonnelFormValues = {
	first_name: "",
	last_name: "",
	email: "",
	position: "",
	experience: "",
	sort_order: null,
	sector_ids: [],
	disciplines: [],
	avatar_image: null,
	projects_completed: [],
	resume_files: [],
	is_resume_private: false,
};

const mapDetailToFormValues = (detail: KeyPersonnelDetail): KeyPersonnelFormValues => ({
	first_name: detail.first_name,
	last_name: detail.last_name,
	email: detail.email,
	position: detail.position,
	experience: detail.experience || "",
	sort_order: detail.sort_order,
	sector_ids: detail.sectors.map((s) => s.id),
	disciplines: detail.disciplinesFormatted?.length
		? detail.disciplinesFormatted
		: [],
	avatar_image: detail.avatar_location,
	projects_completed: detail.projects_completed,
	resume_files: detail.resume.map((r) => ({
		id: r.id,
		is_default: r.is_default,
	})),
	is_resume_private: detail.is_resume_private
});

const KeyPersonnelFormPage: React.FC<KeyPersonnelFormPageProps> = ({ id }) => {
	const isNew = !id;
	const [createdId, setCreatedId] = useState<number | null>(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	const { memberPaths } = useNavigationPaths();
	const keyPersonnelPaths = memberPaths?.companyProfile;

	const effectiveId = createdId ?? (id ? Number(id) : null);

	const { data: detail, isLoading } = useKeyPersonnelDetail(
		effectiveId ?? 0
	);

	const createMutation = useCreateKeyPersonnel();
	const updateMutation = useUpdateKeyPersonnel(effectiveId ?? 0);
	const deleteMutation = useDeleteKeyPersonnel();

	const currentCompany = useCurrentCompany();

	// Profile type from the detail or default (2 = HC)
	const profileTypeId = currentCompany?.profile_type?.id ?? 2;

	const validationSchema = useMemo(
		() => createKeyPersonnelValidation(profileTypeId),
		[profileTypeId]
	);

	const initialValues = useMemo<KeyPersonnelFormValues>(() => {
		if (detail && !isNew) {
			return mapDetailToFormValues(detail);
		}
		return emptyInitialValues;
	}, [detail, isNew]);

	const handleSubmit = useCallback(
		async (values: KeyPersonnelFormValues) => {
			try {
				const fd = buildFormData(values);
				if (isNew && !createdId) {
					const result = await createMutation.mutateAsync(fd);
					const newId = (result as any)?.id;
					if (newId && keyPersonnelPaths) {
						setCreatedId(newId);
						navigate(keyPersonnelPaths.keyPersonnelEdit(newId), { replace: true });
					}
				} else if (effectiveId) {
					await updateMutation.mutateAsync(fd);
					showToast("Changes saved.", "Key Personnel", true);
				}
			} catch (error: any) {
				const message =
					error?.response?.data?.message ||
					error?.message ||
					"Failed to save changes.";
				showToast(message, "Key Personnel", false);
			}
		},
		[isNew, createdId, effectiveId, createMutation, updateMutation, keyPersonnelPaths]
	);

	const handleConfirmDelete = useCallback(() => {
		if (!effectiveId) return;
		deleteMutation.mutate(effectiveId, {
			onSuccess: () => {
				setShowDeleteModal(false);
				if (keyPersonnelPaths) {
					navigate(keyPersonnelPaths.keyPersonnel);
				}
			},
		});
	}, [effectiveId, deleteMutation, keyPersonnelPaths]);

	if (!isNew && isLoading) {
		return (
			<div className="flex items-center justify-center py-16" data-testid="loading-state">
				<Spinner size="lg" />
			</div>
		);
	}

	const content = (
		<div className="flex flex-col gap-4" data-testid="key-personnel-form-page">
			<TabHeader
				heading={<div className="flex gap-4 flex-row">
					<BackButton navigateTo={keyPersonnelPaths.keyPersonnel} />
					{
						isNew
							? "Create Key Personnel"
							: `Edit ${detail?.first_name?.trim() ?? ""} ${detail?.last_name?.trim() ?? ""}`
					}
				</div>
				}
				description={<AutoSaveIndicator />}
			/>

			<Formik<KeyPersonnelFormValues>
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ isValid, dirty }) => (
					<Form>
						{!isNew && <FormikAutoSave debounceMs={5000} />}

						<KeyPersonnelFullForm
							profileTypeId={profileTypeId}
							existingResumes={detail?.resume ?? []}
						/>
						<div className="pt-2">
							<InstructionsInfoBox inline>
								You can upload multiple resumes. Click the star icon to set a default resume that will be shown on your profile.
							</InstructionsInfoBox>
						</div>

						{/* validation message */}
						{!isValid && dirty && <div className="flex justify-end text-red-600">Complete all required fields before saving</div>}

						{/* Action buttons */}
						<div className="mt-6 flex justify-between">
							{!isNew && effectiveId ? (
								<Button
									btntype="third-danger"
									onClick={() => setShowDeleteModal(true)}
									disabled={deleteMutation.isPending}
									data-testid="delete-btn"
								>
									Delete
								</Button>
							) : <div />}
							<div className="flex items-center gap-2">
								<AutoSaveIndicator />
								<Tooltip label={dirty ? isValid ? "Save changes" : "Complete all required fields before saving" : "No changes to save"}>
									<button
										type="submit"
										disabled={!dirty || createMutation.isPending || updateMutation.isPending}
										className="flex items-center gap-2 rounded bg-blue-600 px-6 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
										data-testid={isNew ? "save-continue-btn" : "save-btn"}
									>
										{(createMutation.isPending || updateMutation.isPending) ? (
											<Spinner size="sm" />
										) : isNew ? (
											"Save & Continue"
										) : (
											"Save Changes"
										)}
									</button>
								</Tooltip>
							</div>
						</div>
					</Form>
				)}
			</Formik>

			{/* Delete confirmation modal */}
			<Modal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				title="Delete Key Personnel"
				description="Please confirm deletion"
				iconType="warning"
				titleColor="red.600"
				size="md"
				customFooter={
					<div className="flex w-full justify-between gap-2">
						<Button
							onClick={() => setShowDeleteModal(false)}
							btntype="base"
							className="!w-full"
							disabled={deleteMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							className="!w-full"
							btntype="third-danger"
							onClick={handleConfirmDelete}
							disabled={deleteMutation.isPending}
							data-testid="confirm-delete-button"
						>
							{deleteMutation.isPending ? <Spinner size="sm" /> : "Delete"}
						</Button>
					</div>
				}
			>
				{detail && (
					<p className="text-sm text-gray-600" data-testid="delete-confirmation-text">
						Are you sure you want to delete{" "}
						<span className="font-medium">{detail.first_name} {detail.last_name}</span>?
						This action cannot be undone.
					</p>
				)}
			</Modal>
		</div>
	);

	// Wrap in AutoSaveProvider for edit mode so FormikAutoSave and AutoSaveIndicator share state
	if (!isNew) {
		return <AutoSaveProvider>{content}</AutoSaveProvider>;
	}

	return content;
};

export { KeyPersonnelFormPage };
